import type { OrganizationPublic, PublicEvent } from '@irace/shared';
import { EventBanner } from '../components/EventBanner.tsx';
import { EventInfoPanel } from '../components/EventInfoPanel.tsx';
import { PoweredByFooter } from '../components/PoweredByFooter.tsx';
import { COPY } from '../copy/vi.ts';
import { formatOpensAt } from '../lib/format.ts';

export type UnavailableReason = 'scheduled' | 'closed' | 'not_found';

const TEXT: Record<UnavailableReason, { message: string; sub: string }> = {
  scheduled: { message: COPY.unavailable.scheduled, sub: COPY.unavailable.scheduledSub },
  closed: { message: COPY.unavailable.closed, sub: COPY.unavailable.closedSub },
  not_found: { message: COPY.unavailable.notFound, sub: COPY.unavailable.notFoundSub },
};

/**
 * M01 (§6, §28.2, §28.3).
 *
 * No form is rendered in any variant — a participant must not be able to fill
 * in details the server will refuse.
 */
export function EventUnavailable({
  reason,
  event,
  organization,
}: {
  reason: UnavailableReason;
  event?: PublicEvent;
  /** Absent on `not_found`, where there is no event to have an organizer. */
  organization?: OrganizationPublic | null;
}) {
  const { message, sub } = TEXT[reason];
  // §28.3 — pill only on the scheduled variant, and only when the organizer
  // actually published a start time.
  const opensAt =
    reason === 'scheduled' && event ? formatOpensAt(event.starts_at, event.timezone) : null;

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col bg-surface">
      {event ? <EventBanner event={event} /> : null}
      <div className="flex flex-1 flex-col items-center justify-center gap-5 p-8 text-center">
        <div
          className="flex size-14 items-center justify-center rounded-full bg-[#eef2f7] text-2xl font-bold text-text-muted"
          aria-hidden="true"
        >
          i
        </div>
        <h1 className="text-xl font-bold" role="status">
          {message}
        </h1>
        {opensAt ? (
          <p className="rounded-full border border-[#bdd6ff] bg-[#ebf2ff] px-4 py-2 text-sm font-bold text-[#0052cc]">
            {COPY.unavailable.opensAtPrefix} {opensAt}
          </p>
        ) : null}
        <p className="max-w-70 text-sm leading-relaxed text-text-muted">{sub}</p>
      </div>

      {event ? (
        <div className="px-5">
          {/* The pill above already states the start time on this variant, so
              the panel contributes the organizer and the event link only. */}
          <EventInfoPanel
            event={event}
            organization={organization}
            showSchedule={reason !== 'scheduled'}
          />
        </div>
      ) : null}

      <div className="p-5">
        <PoweredByFooter organization={organization} />
      </div>
    </div>
  );
}
